import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Users as UsersIcon, DollarSign, Activity, HeartPulse, ToggleLeft, TrendingDown, ShieldCheck, History, Mail, BookOpen, LayoutDashboard, FlaskConical, Ticket, LifeBuoy, BadgeDollarSign, Sparkles, Mic, Gift, MessageSquare, Target, Phone, Calculator, Rocket, Share2, Webhook, Wand2, Trash2, Radio, LogOut, PlayCircle } from "lucide-react";

const TABS = [
  { to: "/v3/admin/console", label: "Overview", icon: LayoutDashboard, end: true, id: "admin-overview" },
  { to: "/v3/admin/console/users", label: "Users", icon: UsersIcon, id: "admin-users" },
  { to: "/v3/admin/console/user-cleanup", label: "User Cleanup", icon: Trash2, id: "admin-user-cleanup" },
  { to: "/v3/admin/console/inbound-webhooks", label: "Inbound Webhooks", icon: Radio, id: "admin-inbound-webhooks" },
  { to: "/v3/admin/console/revenue", label: "Revenue", icon: DollarSign, id: "admin-revenue" },
  { to: "/v3/admin/console/valuation", label: "Valuation", icon: Calculator, id: "admin-valuation" },
  { to: "/v3/admin/console/launch-kit", label: "Launch Kit", icon: Rocket, id: "admin-launch-kit" },
  { to: "/v3/admin/console/outreach-templates", label: "Outreach Templates", icon: MessageSquare, id: "admin-outreach-templates" },
  { to: "/v3/admin/console/social-media", label: "Social Media", icon: Share2, id: "admin-social-media" },
  { to: "/v3/admin/console/marketing/motivated-seller", label: "Flagship A/B", icon: FlaskConical, id: "admin-motivated-seller-ab" },
  { to: "/v3/admin/console/content-studio", label: "Content Studio", icon: Wand2, id: "admin-content-studio" },
  { to: "/v3/admin/console/vip-links", label: "VIP Links", icon: Ticket, id: "admin-vip-links" },
  { to: "/v3/admin/console/affiliates", label: "Affiliates", icon: BadgeDollarSign, id: "admin-affiliates" },
  { to: "/v3/admin/console/podcasters", label: "Podcasters", icon: Mic, id: "admin-podcasters" },
  { to: "/v3/admin/console/changelog", label: "Changelog", icon: Gift, id: "admin-changelog" },
  { to: "/v3/admin/console/ebook-survey", label: "Ebook Survey", icon: MessageSquare, id: "admin-ebook-survey" },
  { to: "/v3/admin/console/marketplace", label: "Marketplace", icon: Target, id: "admin-marketplace" },
  { to: "/v3/admin/console/apex-calls", label: "Apex Calls", icon: Phone, id: "admin-apex-calls" },
  { to: "/v3/admin/console/services", label: "Services", icon: Sparkles, id: "admin-services" },
  { to: "/v3/admin/console/support", label: "Support", icon: LifeBuoy, id: "admin-support" },
  { to: "/v3/admin/console/activity", label: "Activity", icon: Activity, id: "admin-activity" },
  { to: "/v3/admin/console/funnel", label: "Funnel", icon: TrendingDown, id: "admin-funnel" },
  { to: "/v3/admin/console/demo-funnel", label: "Demo Funnel", icon: PlayCircle, id: "admin-demo-funnel" },
  { to: "/v3/admin/console/audit", label: "Audit Log", icon: History, id: "admin-audit" },
  { to: "/v3/admin/console/health", label: "System Health", icon: HeartPulse, id: "admin-health" },
  { to: "/v3/admin/console/webhooks", label: "Stripe Webhooks", icon: Webhook, id: "admin-webhooks" },
  { to: "/v3/admin/console/flags", label: "Feature Flags", icon: ToggleLeft, id: "admin-flags" },
  { to: "/v3/admin/console/resources", label: "Resources", icon: BookOpen, id: "admin-resources" },
  { to: "/v3/admin/console/email", label: "Email Ops", icon: Mail, id: "admin-email-ops" },
  { to: "/v3/admin/console/ab-tests", label: "A/B Tests", icon: FlaskConical, id: "admin-ab-tests" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (user && user.role !== "admin") {
      nav("/v3", { replace: true });
    }
  }, [user, nav]);

  if (!user || user.role !== "admin") {
    return null;
  }

  const handleSignOut = async () => {
    try { await logout(); } finally { nav("/login", { replace: true }); }
  };

  return (
    <div data-testid="admin-panel">
      <div className="topbar">
        <div className="flex items-start gap-4 min-w-0">
          <Link to="/v3" data-testid="admin-brand-link"
                title="Back to workspace" className="shrink-0">
            <img
              src="/brand/wordmark-color.png?v=2"
              alt="iSpeedToClose"
              width="360" height="90"
              draggable={false}
              className="h-16 md:h-20 w-auto select-none drop-shadow-md"
            />
          </Link>
          <div className="min-w-0">
            <div className="crumb flex items-center gap-2">
              <ShieldCheck size={12} /> Admin
            </div>
            <h1 className="font-display font-extrabold text-3xl tracking-tight mt-1">
              Admin Console
            </h1>
            <p className="text-[var(--ink-2)] text-sm mt-1">
              Manage users, revenue, system health and feature flags for iSpeedToClose.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="badge" style={{ background: "#0B132B", color: "#fff" }}>
            {user.email}
          </div>
          <button
            type="button"
            onClick={handleSignOut}
            data-testid="admin-signout-btn"
            title="Sign out of iSpeedToClose"
            className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold uppercase tracking-wider px-3 py-2 shadow-md transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </div>

      <div className="card p-1 mb-4 flex flex-wrap gap-1 overflow-x-auto" data-testid="admin-tabs">
        {TABS.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`tab-${t.id}`}
            className={({ isActive }) =>
              "flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm whitespace-nowrap transition-colors " +
              (isActive
                ? "bg-[var(--primary)] text-white font-semibold"
                : "text-[var(--ink-2)] hover:bg-slate-100")
            }
          >
            <t.icon size={14} />
            <span>{t.label}</span>
          </NavLink>
        ))}
      </div>

      <Outlet />
    </div>
  );
}
